import * as React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { MegaphoneIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { KeycloakContext } from '../../App';
import { API_URL } from '../../runtimeConfig';
import { formatDate } from '../../lib/format';
import { cn } from '../../lib/cn';

export interface PatchNote {
  id: number;
  version: string;
  title: string;
  body: string;
  published: boolean;
  published_at?: string | null;
  created_at: string;
  updated_at: string;
}

const SEEN_KEY = 'edj-dashboard-patch-notes-seen';

function readSeenId(): number | null {
  try {
    const raw = window.localStorage.getItem(SEEN_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeSeenId(id: number) {
  try { window.localStorage.setItem(SEEN_KEY, String(id)); } catch { /* ignore */ }
}

/**
 * PatchNotesButton — topbar icon that opens a "What's new" modal listing the
 * platform's recent changelog entries. Shows an unread dot when the latest
 * entry's id differs from the one stored in localStorage.
 */
export function PatchNotesButton() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const [notes, setNotes] = useState<PatchNote[]>([]);
  const [open, setOpen] = useState(false);
  const [seenId, setSeenId] = useState<number | null>(() => readSeenId());

  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/patch-notes`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { if (alive) setNotes(Array.isArray(data) ? data : []); })
      .catch(() => { /* silent — modal will just be empty */ });
    return () => { alive = false; };
    // Re-fetch on token change so freshly-logged-in users get fresh data via
    // any future authenticated path; the public endpoint itself doesn't need it.
  }, [keycloak?.token]);

  const latestId = notes[0]?.id ?? null;
  const hasUnread = latestId !== null && latestId !== seenId;

  const handleOpen = () => {
    setOpen(true);
    if (latestId !== null) {
      writeSeenId(latestId);
      setSeenId(latestId);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        aria-label="What's new"
        title="What's new"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground hover:bg-accent transition-colors"
      >
        <MegaphoneIcon className="h-4 w-4" />
        {hasUnread && (
          <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary ring-2 ring-background" aria-hidden="true" />
        )}
      </button>

      {open && createPortal(
        <PatchNotesModal notes={notes} onClose={() => setOpen(false)} />,
        document.body,
      )}
    </>
  );
}

function PatchNotesModal({ notes, onClose }: { notes: PatchNote[]; onClose: () => void }) {
  // Esc to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8" role="dialog" aria-modal="true" aria-labelledby="patch-notes-title">
      <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-lg border border-border bg-card text-card-foreground shadow-elevation-3 animate-fade-in-up">
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
              <MegaphoneIcon className="h-5 w-5" />
            </span>
            <div>
              <h2 id="patch-notes-title" className="text-base font-semibold text-foreground">What's new</h2>
              <p className="text-xs text-muted-foreground">Recent changes shipped to your dashboard.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {notes.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No patch notes published yet.</p>
          ) : (
            <ul className="space-y-6">
              {notes.map((note) => (
                <li key={note.id} className="border-b border-border pb-5 last:border-b-0 last:pb-0">
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <div className="flex items-baseline gap-2">
                      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-mono font-medium bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
                        {note.version}
                      </span>
                      <h3 className="text-sm font-semibold text-foreground">{note.title}</h3>
                    </div>
                    <time className="text-xs text-muted-foreground shrink-0">
                      {formatDate(note.published_at || note.created_at)}
                    </time>
                  </div>
                  <Markdown text={note.body} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiny markdown renderer — bold (**…**) / italic (*…* or _…_) / inline code
// (`…`) / links ([text](url)) / unordered lists (- … per line). No external
// dependency; enough for changelog content. Anything else renders as plain
// text — escape HTML to stay safe.

function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderInline(s: string): string {
  let out = escapeHtml(s);
  // links — [text](url) → <a href="url">text</a>. URL is sanitized: must start
  // with http(s):// or be a same-origin path.
  out = out.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+|\/[^)\s]*)\)/g,
    (_m, text: string, url: string) => `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">${text}</a>`);
  out = out.replace(/`([^`]+)`/g, '<code class="bg-muted px-1 py-0.5 rounded text-[0.8125rem] font-mono">$1</code>');
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
  out = out.replace(/(^|[^_])_([^_\n]+)_(?!_)/g, '$1<em>$2</em>');
  return out;
}

function Markdown({ text }: { text: string }) {
  const html = useMemo(() => {
    if (!text) return '';
    const lines = text.split('\n');
    const blocks: string[] = [];
    let listBuffer: string[] = [];
    const flushList = () => {
      if (listBuffer.length === 0) return;
      blocks.push(`<ul class="list-disc pl-5 space-y-1 text-sm text-foreground">${listBuffer.map((li) => `<li>${renderInline(li)}</li>`).join('')}</ul>`);
      listBuffer = [];
    };
    for (const raw of lines) {
      const line = raw.replace(/\s+$/g, '');
      if (/^\s*[-*]\s+/.test(line)) {
        listBuffer.push(line.replace(/^\s*[-*]\s+/, ''));
        continue;
      }
      flushList();
      if (line.trim() === '') {
        blocks.push('<div class="h-2"></div>');
      } else {
        blocks.push(`<p class="text-sm text-foreground">${renderInline(line)}</p>`);
      }
    }
    flushList();
    return blocks.join('');
  }, [text]);

  return <div className={cn('space-y-1.5')} dangerouslySetInnerHTML={{ __html: html }} />;
}
